import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, RefreshCw, Copy, Check, Eye, X, Pencil, Trash2, ChevronDown, ArrowUp, ArrowDown, ArrowUpDown, Download } from "lucide-react";
import { updateBarcodeRegistrySku, getBomBarcodeRegistry } from "../api/bomApi";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { exportToExcel } from "../utils/exportToExcel";
const ITEMS_PER_PAGE = 50;
const normalizeIndicatorNumber = (value) => {
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};
const getNormalizedBarcodeDetails = (record = {}) => {
  const unitsByFormat =
    record?.unitsByFormat && typeof record.unitsByFormat === "object"
      ? record.unitsByFormat
      : record?.formatUnits && typeof record.formatUnits === "object"
        ? record.formatUnits
        : {};
  const incomingSccByIndicator =
    record?.sccByIndicator && typeof record.sccByIndicator === "object"
      ? record.sccByIndicator
      : {};

  const indicatorSet = new Set();
  Object.keys(unitsByFormat).forEach((key) => {
    const indicator = normalizeIndicatorNumber(key);
    if (indicator !== null && indicator > 0) indicatorSet.add(indicator);
  });
  Object.keys(incomingSccByIndicator).forEach((key) => {
    const indicator = normalizeIndicatorNumber(key);
    if (indicator !== null && indicator > 0) indicatorSet.add(indicator);
  });
  if (Array.isArray(record?.sccIndicators)) {
    record.sccIndicators.forEach((value) => {
      const indicator = normalizeIndicatorNumber(value);
      if (indicator !== null && indicator > 0) indicatorSet.add(indicator);
    });
  }
  const singleIndicator = normalizeIndicatorNumber(record?.sccIndicator);
  if (singleIndicator !== null && singleIndicator > 0) {
    indicatorSet.add(singleIndicator);
  }

  const hasTopLevelCodes = Boolean(record?.scc14 || record?.gs1128);
  const fallbackIndicator =
    (singleIndicator && singleIndicator > 0)
      ? singleIndicator
      : indicatorSet.size > 0
        ? Math.min(...Array.from(indicatorSet))
        : 10;

  const sccByIndicator = { ...incomingSccByIndicator };
  if (
    hasTopLevelCodes &&
    (!sccByIndicator[String(fallbackIndicator)] ||
      (!sccByIndicator[String(fallbackIndicator)]?.scc14 &&
        !sccByIndicator[String(fallbackIndicator)]?.gs1128))
  ) {
    sccByIndicator[String(fallbackIndicator)] = {
      ...(sccByIndicator[String(fallbackIndicator)] || {}),
      scc14: record?.scc14 || "",
      gs1128: record?.gs1128 || "",
      indicator: fallbackIndicator,
    };
    indicatorSet.add(fallbackIndicator);
  }

  const indicators = Array.from(indicatorSet).sort((a, b) => a - b);
  const rows = indicators
    .map((indicator) => {
      const key = String(indicator);
      const entry = sccByIndicator[key] || {};
      const unit = String(unitsByFormat[key] || "").trim();
      const scc14 = String(entry?.scc14 || "").trim();
      const gs1128 = String(entry?.gs1128 || "").trim();
      return {
        indicator,
        unit,
        scc14,
        gs1128,
        existsInErpag: entry?.existsInErpag !== undefined ? Boolean(entry.existsInErpag) : null,
      };
    })
    .filter((row) => row.scc14 || row.gs1128 || row.unit);

  const summary = rows.map((row) =>
    row.unit ? `${row.indicator} (${row.unit})` : String(row.indicator),
  );
  const hasAnyCode = rows.some((row) => row.scc14 || row.gs1128);

  return {
    rows,
    summary,
    hasAnyCode,
  };
};

export default function UPCGenerated({
  upcs = [],
  loading = false,
  error = "",
  onRefresh = () => {},
  onQueryChange = () => {},
  onPageChange = () => {},
  onPageSizeChange = () => {},
  sortField: externalSortField = "",
  sortOrder: externalSortOrder = "asc",
  onSortChange = () => {},
  totalRecords = 0,
  apiPage = 1,
  apiPageSize = 50,
  categories = [],
  systemFilter = "all",
  erpagFilter = "all",
  canEditSku = false,
  isAdmin = false,
  onDeleteRow = null,
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [copied, setCopied] = useState(false);
  const [copiedColumnKey, setCopiedColumnKey] = useState(null);
  const [copiedFormatColumn, setCopiedFormatColumn] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(apiPageSize || 50);
  const [searchInput, setSearchInput] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const categoryRef = useRef(null);
  const [selectedSystemFilter, setSelectedSystemFilter] = useState(
    String(systemFilter || "all"),
  );
  const [selectedErpagFilter, setSelectedErpagFilter] = useState(
    String(erpagFilter || "all"),
  );
  const [selectedUomBarcodeFilter, setSelectedUomBarcodeFilter] = useState("all");
  const [viewingRecord, setViewingRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editingSku, setEditingSku] = useState("");
  const [savingSku, setSavingSku] = useState(false);
  const [skuEditError, setSkuEditError] = useState("");
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [deletingRow, setDeletingRow] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [exportingMissing, setExportingMissing] = useState(false);
  const didMountQueryRef = useRef(false);

  const [internalSortField, setInternalSortField] = useState(null);
  const [internalSortOrder, setInternalSortOrder] = useState("asc");

  const sortField =
    externalSortField !== undefined && externalSortField !== null && externalSortField !== ""
      ? externalSortField
      : internalSortField;
  const sortOrder = externalSortOrder || internalSortOrder;

  const handleSort = (field) => {
    let nextOrder = "asc";
    if (sortField === field) {
      nextOrder = sortOrder === "asc" ? "desc" : "asc";
    }
    setInternalSortField(field);
    setInternalSortOrder(nextOrder);
    setCurrentPage(1);
    if (typeof onSortChange === "function") {
      onSortChange({ sortField: field, sortOrder: nextOrder });
    }
  };


  useEffect(() => {
    if (apiPageSize && apiPageSize !== itemsPerPage) {
      setItemsPerPage(apiPageSize);
    }
  }, [apiPageSize]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const safeUpcs = Array.isArray(upcs) ? upcs : [];
  const pagesPerApiCall = Math.max(Math.floor(apiPageSize / itemsPerPage), 1);
  const totalPages = Math.max(
    1,
    Math.ceil((Number(totalRecords) || 0) / itemsPerPage),
  );

  const categoryOptions = useMemo(() => {
    const normalized = Array.isArray(categories)
      ? categories.map((category) => String(category || "").trim()).filter(Boolean)
      : [];
    return ["all", ...normalized];
  }, [categories]);

  const withinApiPage = (currentPage - 1) % pagesPerApiCall;
  const startIndex = withinApiPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = useMemo(
    () => safeUpcs.slice(startIndex, endIndex),
    [safeUpcs, startIndex, endIndex],
  );
  const currentItemsWithUomType = useMemo(() => {
    const baseUnits = new Set(["UN", "KG", "G", "ML", "L"]);
    const groupedBySku = new Map();

    // Group items by SKU so multiple codes/rows for the same SKU are combined into ONE item
    currentItems.forEach((record) => {
      const skuKey = String(record?.sku || "").trim();
      // If SKU is empty, keep as standalone row
      if (!skuKey) {
        if (!groupedBySku.has("__empty__")) groupedBySku.set("__empty__", []);
        groupedBySku.get("__empty__").push(record);
        return;
      }
      if (!groupedBySku.has(skuKey)) groupedBySku.set(skuKey, []);
      groupedBySku.get(skuKey).push(record);
    });

    const consolidatedItems = [];

    groupedBySku.forEach((records, skuKey) => {
      if (skuKey === "__empty__") {
        records.forEach((rec) => consolidatedItems.push(rec));
        return;
      }

      if (records.length === 1) {
        consolidatedItems.push(records[0]);
        return;
      }

      // Rank records: prefer base units (KG, UN, etc.) as primary Base UPC row
      const sorted = [...records].sort((a, b) => {
        const unitA = String(a?.unit || a?.upcUnit || "").trim().toUpperCase();
        const unitB = String(b?.unit || b?.upcUnit || "").trim().toUpperCase();
        const rankA = baseUnits.has(unitA) ? 0 : 1;
        const rankB = baseUnits.has(unitB) ? 0 : 1;
        if (rankA !== rankB) return rankA - rankB;
        return 0;
      });

      const primary = sorted[0];
      const secondary = sorted[1]; // e.g., the second row with the second barcode code

      // Combine SCC and other indicators across all records for this SKU
      const combinedSccByIndicator = {};
      const combinedUnitsByFormat = {};
      records.forEach((rec) => {
        Object.assign(combinedSccByIndicator, rec?.sccByIndicator || {});
        Object.assign(combinedUnitsByFormat, rec?.unitsByFormat || {});
      });

      // Dynamically attach up to 5 UOM levels (Base = 0, 2nd = 10, 3rd = 20, 4th = 30, 5th = 40)
      const uomIndicators = [10, 20, 30, 40, 50];
      const defaultUnits = ["CS", "Master", "Case", "Pallet"];
      const primaryCleanUpc = String(primary?.upc || primary?.upcFull || "").replace(/\D/g, "");

      for (let i = 1; i < Math.min(sorted.length, 5); i++) {
        const uomRecord = sorted[i];
        const ind = uomIndicators[i - 1];
        const uomUpc = String(uomRecord?.upc || uomRecord?.upcFull || "").trim();
        const uomCleanUpc = uomUpc.replace(/\D/g, "");
        const isSameBaseUpc = primaryCleanUpc && uomCleanUpc && (
          primaryCleanUpc === uomCleanUpc ||
          primaryCleanUpc === `0${uomCleanUpc}` ||
          `0${primaryCleanUpc}` === uomCleanUpc
        );
        const fallbackUnit = defaultUnits[i - 1] || `UOM ${i + 1}`;
        const uomUnit = uomRecord?.unit || uomRecord?.upcUnit || fallbackUnit;

        if (uomUpc && !isSameBaseUpc && !combinedSccByIndicator[String(ind)]?.scc14) {
          combinedSccByIndicator[String(ind)] = {
            indicator: ind,
            scc14: uomUpc,
            unit: uomUnit,
          };
          if (!combinedUnitsByFormat[String(ind)]) {
            combinedUnitsByFormat[String(ind)] = uomUnit;
          }
        }
      }

      consolidatedItems.push({
        ...primary,
        sccByIndicator: combinedSccByIndicator,
        unitsByFormat: combinedUnitsByFormat,
        unit: primary.unit || "kg",
      });
    });

    const processed = consolidatedItems.map((record) => {
      const unitValue = String(record?.unit || record?.upcUnit || "")
        .trim()
        .toUpperCase();
      const uomType = "UOM";
      const barcodeDetails = getNormalizedBarcodeDetails(record);
      const rows = barcodeDetails.rows || [];
      const secondUomEntry = rows.find((r) => r.indicator === 10);
      const thirdUomEntry = rows.find((r) => r.indicator === 20);
      const otherUoms = rows.filter((r) => r.indicator !== 10 && r.indicator !== 20);

      const secondUomDisplay = secondUomEntry
        ? (secondUomEntry.scc14 || "-")
        : "-";
      const thirdUomDisplay = thirdUomEntry
        ? (thirdUomEntry.scc14 || "-")
        : "-";

      const baseBarcodeExists = record?.baseExistsInErpag !== undefined ? Boolean(record.baseExistsInErpag) : Boolean(record?.existsInErpag);
      const secondBarcodeExists = secondUomEntry ? Boolean(secondUomEntry.existsInErpag) : null;
      const thirdBarcodeExists = thirdUomEntry ? Boolean(thirdUomEntry.existsInErpag) : null;

      // 1. Check if SKU itself exists in ERPAG (from backend record.existsInErpag)
      const skuExistsInErpag = Boolean(record?.existsInErpag || record?.erpagProduct);
      let rowErpagStatus = "not_in_erpag"; // default

      if (skuExistsInErpag) {
        rowErpagStatus = "fully_exists"; // SKU exists in ERPAG
      } else {
        rowErpagStatus = "not_in_erpag";
      }

      return {
        ...record,
        uomType,
        uomTypeDisplay: unitValue ? `${uomType} (${unitValue})` : uomType,
        formatSummary: barcodeDetails.summary,
        formatRows: barcodeDetails.rows,
        hasFormatCodes: barcodeDetails.hasAnyCode,
        secondUomEntry,
        thirdUomEntry,
        otherUoms,
        secondUomDisplay,
        thirdUomDisplay,
        baseBarcodeExists,
        secondBarcodeExists,
        thirdBarcodeExists,
        skuExistsInErpag,
        rowErpagStatus,
      };
    });

    const filteredByUomBarcode = processed.filter((r) => {
      if (selectedUomBarcodeFilter === "all") return true;
      if (selectedUomBarcodeFilter === "missing_any") {
        const baseMissing = (r.upc || r.upcFull) && !r.baseBarcodeExists;
        const secondMissing = r.secondUomEntry && r.secondUomEntry.scc14 && !r.secondBarcodeExists;
        const thirdMissing = r.thirdUomEntry && r.thirdUomEntry.scc14 && !r.thirdBarcodeExists;
        const otherMissing = Array.isArray(r.otherUoms) && r.otherUoms.some((o) => o.scc14 && o.existsInErpag === false);
        return baseMissing || secondMissing || thirdMissing || otherMissing;
      }
      if (selectedUomBarcodeFilter === "missing_base") {
        return (r.upc || r.upcFull) && !r.baseBarcodeExists;
      }
      if (selectedUomBarcodeFilter === "missing_second") {
        return r.secondUomEntry && r.secondUomEntry.scc14 && !r.secondBarcodeExists;
      }
      if (selectedUomBarcodeFilter === "missing_third") {
        return r.thirdUomEntry && r.thirdUomEntry.scc14 && !r.thirdBarcodeExists;
      }
      if (selectedUomBarcodeFilter === "missing_other") {
        return Array.isArray(r.otherUoms) && r.otherUoms.some((o) => o.scc14 && o.existsInErpag === false);
      }
      return true;
    });

    if (!sortField) return filteredByUomBarcode;

    return [...filteredByUomBarcode].sort((a, b) => {
      let valA = "";
      let valB = "";

      switch (sortField) {
        case "sku":
          valA = String(a?.sku || "").trim();
          valB = String(b?.sku || "").trim();
          return sortOrder === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        case "name":
          valA = String(a?.productName || a?.nameEn || a?.name || "").trim().toLowerCase();
          valB = String(b?.productName || b?.nameEn || b?.name || "").trim().toLowerCase();
          break;
        case "erpagStatus":
          valA = Boolean(a?.existsInErpag || a?.erpagProduct) ? "1" : "0";
          valB = Boolean(b?.existsInErpag || b?.erpagProduct) ? "1" : "0";
          break;
        case "category":
          valA = String(a?.category || a?.itemCategory || "").trim().toLowerCase();
          valB = String(b?.category || b?.itemCategory || "").trim().toLowerCase();
          break;
        case "uomType":
          valA = String(a?.uomTypeDisplay || a?.unit || "").trim().toLowerCase();
          valB = String(b?.uomTypeDisplay || b?.unit || "").trim().toLowerCase();
          break;
        case "upc":
          valA = String(a?.upcFull || a?.upc || "").trim();
          valB = String(b?.upcFull || b?.upc || "").trim();
          return sortOrder === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        case "createdBy":
          valA = String(a?.createdBy || "").trim().toLowerCase();
          valB = String(b?.createdBy || "").trim().toLowerCase();
          break;
        case "createdAt":
          valA = new Date(a?.createdAt || 0).getTime();
          valB = new Date(b?.createdAt || 0).getTime();
          return sortOrder === "asc" ? valA - valB : valB - valA;
        default:
          return 0;
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [currentItems, sortField, sortOrder]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage, safeUpcs]);

  useEffect(() => {
    if (!categoryOptions.includes(selectedCategory)) {
      setSelectedCategory("all");
    }
  }, [categoryOptions, selectedCategory]);

  useEffect(() => {
    const nextApiPage = Math.ceil(currentPage / pagesPerApiCall);
    if (nextApiPage !== apiPage) {
      onPageChange(currentPage);
    }
  }, [apiPage, currentPage, onPageChange, pagesPerApiCall]);

  const isFirstMountRef = useRef(true);
  const searchDebounceTimerRef = useRef(null);

  useEffect(() => {
    if (!didMountQueryRef.current) {
      didMountQueryRef.current = true;
      return;
    }
    setSelectedSystemFilter(String(systemFilter || "all"));
  }, [systemFilter]);

  useEffect(() => {
    setSelectedErpagFilter(String(erpagFilter || "all"));
  }, [erpagFilter]);

  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }

    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      onQueryChange({
        search: searchInput,
        category: selectedCategory,
        systemFilter: selectedSystemFilter,
        erpagFilter: selectedErpagFilter,
      });
      setCurrentPage(1);
    }, 350);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  const handleFind = () => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
    onQueryChange({
      search: searchInput,
      category: selectedCategory,
      systemFilter: selectedSystemFilter,
      erpagFilter: selectedErpagFilter,
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }
    setSearchInput("");
    setSelectedCategory("all");
    setSelectedSystemFilter("all");
    setSelectedErpagFilter("all");
    setSelectedUomBarcodeFilter("all");
    setCurrentPage(1);
    onQueryChange({
      search: "",
      category: "all",
      systemFilter: "all",
      erpagFilter: "all",
    });
  };

  const toggleSelect = (localIndex) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(localIndex)) {
      newSelected.delete(localIndex);
    } else {
      newSelected.add(localIndex);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectPage = () => {
    const newSelected = new Set(selectedIds);
    const pageIndices = currentItems.map((_, idx) => idx);
    const allPageSelected = pageIndices.every((idx) => newSelected.has(idx));

    if (allPageSelected) {
      pageIndices.forEach((idx) => newSelected.delete(idx));
    } else {
      pageIndices.forEach((idx) => newSelected.add(idx));
    }

    setSelectedIds(newSelected);
  };

  const handleExportFilteredToExcel = () => {
    const sourceRows =
      selectedIds.size > 0
        ? [...selectedIds]
            .sort((a, b) => a - b)
            .map((index) => currentItemsWithUomType[index])
            .filter(Boolean)
        : currentItemsWithUomType;

    if (!sourceRows.length) return;

    const data = sourceRows.map((r) => ({
      "SKU": r.sku || "",
      "Product Name": r.nameEn || r.description || "",
      "Category": r.productCategory || r.category || "",
      "Base UOM": r.uomTypeDisplay || "",
      "Base UPC": r.upc || r.upcFull || "",
      "Base UPC Status": r.baseBarcodeExists ? "Exists" : "Create",
      "Second UOM Code": r.secondUomEntry?.scc14 || "-",
      "Second UOM": r.secondUomEntry?.unit || "CS",
      "Second UOM Status": r.secondUomEntry?.scc14 ? (r.secondBarcodeExists ? "Exists" : "Create") : "-",
      "Third UOM Code": r.thirdUomEntry?.scc14 || "-",
      "Third UOM": r.thirdUomEntry?.unit || "Master",
      "Third UOM Status": r.thirdUomEntry?.scc14 ? (r.thirdBarcodeExists ? "Exists" : "Create") : "-",
      "Overall ERPAG Status": r.existsInErpag ? "Exists in ERPAG" : "Not in ERPAG",
    }));

    exportToExcel(data, `Barcode_Registry_${new Date().toISOString().slice(0, 10)}`);
  };

  const handleExportMissingToExcel = async () => {
    // If rows are manually checked/selected, export only selected ones that are not in ERPAG
    if (selectedIds.size > 0) {
      const sourceRows = [...selectedIds]
        .sort((a, b) => a - b)
        .map((index) => currentItemsWithUomType[index])
        .filter(Boolean);

      const notInErpagRows = sourceRows.filter((r) => r.rowErpagStatus === "not_in_erpag" || !r.existsInErpag);

      if (notInErpagRows.length === 0) {
        alert("No 'Not in ERPAG' records found among selected rows.");
        return;
      }

      const data = notInErpagRows.map((r) => ({
        "SKU": r.sku || "",
        "Product Name": r.nameEn || r.description || "",
        "Category": r.productCategory || r.category || "",
        "ERPAG Status": "Not in ERPAG",
        "Base UOM": r.uomTypeDisplay || "",
        "Base UPC": r.upc || r.upcFull || "",
        "Base UPC Status": r.baseBarcodeExists ? "Exists" : "Create",
        "Second UOM Code": r.secondUomEntry?.scc14 || "-",
        "Second UOM": r.secondUomEntry?.unit || "CS",
        "Second UOM Status": r.secondUomEntry?.scc14 ? (r.secondBarcodeExists ? "Exists" : "Create") : "-",
        "Third UOM Code": r.thirdUomEntry?.scc14 || "-",
        "Third UOM": r.thirdUomEntry?.unit || "Master",
        "Third UOM Status": r.thirdUomEntry?.scc14 ? (r.thirdBarcodeExists ? "Exists" : "Create") : "-",
      }));

      exportToExcel(data, `Not_In_ERPAG_${new Date().toISOString().slice(0, 10)}`);
      return;
    }

    // Otherwise, fetch ENTIRE dataset of "Not in ERPAG" from backend (across all pages)
    setExportingMissing(true);
    try {
      const response = await getBomBarcodeRegistry({
        page: 1,
        limit: 5000,
        erpagStatus: "not_exists",
        category: selectedCategory !== "all" ? selectedCategory : undefined,
        systemGenerated:
          selectedSystemFilter === "system"
            ? "true"
            : selectedSystemFilter === "bom"
              ? "false"
              : undefined,
        search: searchInput.trim() || undefined,
      });

      const allRecords = (Array.isArray(response?.data) ? response.data : [])
        .filter((r) => !r.existsInErpag);

      if (allRecords.length === 0) {
        alert("No records found that are 'Not in ERPAG'.");
        return;
      }

      const data = allRecords.map((r) => {
        const details = getNormalizedBarcodeDetails(r);
        const secondEntry = (details.rows || []).find((row) => row.indicator === 10);
        const thirdEntry = (details.rows || []).find((row) => row.indicator === 20);

        return {
          "SKU": r.sku || "",
          "Product Name": r.nameEn || r.description || "",
          "Category": r.productCategory || r.category || "",
          "ERPAG Status": "Not in ERPAG",
          "Base UOM": r.unit || "un.",
          "Base UPC": r.upc || r.upcFull || "",
          "Base UPC Status": r.baseExistsInErpag ? "Exists" : "Create",
          "Second UOM Code": secondEntry?.scc14 || r.scc14 || "-",
          "Second UOM": secondEntry?.unit || "CS",
          "Second UOM Status": secondEntry?.existsInErpag ? "Exists" : "Create",
          "Third UOM Code": thirdEntry?.scc14 || "-",
          "Third UOM": thirdEntry?.unit || "Master",
          "Third UOM Status": thirdEntry?.existsInErpag ? "Exists" : "Create",
        };
      });

      exportToExcel(data, `All_Not_In_ERPAG_${new Date().toISOString().slice(0, 10)}`);
    } catch (err) {
      console.error("Export all not in erpag failed:", err);
      alert("Failed to export entire missing data: " + (err?.message || "Unknown error"));
    } finally {
      setExportingMissing(false);
    }
  };

  const handleCopySelected = () => {
    if (selectedIds.size === 0) return;

    const selectedRows = [...selectedIds]
      .sort((a, b) => a - b)
      .map((index) => currentItemsWithUomType[index])
      .filter(Boolean);

    const rows = selectedRows.map((r) => [
      r.sku || "",
      r.nameEn || r.description || "",
      r.rowErpagStatus === "fully_exists"
        ? "Exists in ERPAG"
        : r.rowErpagStatus === "pending_barcodes"
        ? "Pending Barcodes"
        : "Not in ERPAG",
      r.productCategory || r.category || "",
      r.uomTypeDisplay || "",
      r.upc || r.upcFull || "",
      r.secondUomDisplay || "-",
      r.thirdUomDisplay || "-",
    ]);

    const tsv = rows.map((row) => row.join("\t")).join("\n");

    navigator.clipboard.writeText(tsv).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const handleCopyColumn = async (columnKey) => {
    // If rows are selected, copy for selected rows; otherwise copy all visible rows on current page
    const sourceRows =
      selectedIds.size > 0
        ? [...selectedIds]
            .sort((a, b) => a - b)
            .map((index) => currentItemsWithUomType[index])
            .filter(Boolean)
        : currentItemsWithUomType;

    const values = sourceRows.map((r) => {
      switch (columnKey) {
        case "sku":
          return r.sku || "";
        case "name":
          return r.nameEn || r.description || "";
        case "erpagStatus":
          return r.rowErpagStatus === "fully_exists"
            ? "Exists in ERPAG"
            : r.rowErpagStatus === "pending_barcodes"
            ? "Pending Barcodes"
            : "Not in ERPAG";
        case "category":
          return r.productCategory || r.category || "";
        case "uomType":
          return r.uomTypeDisplay || "";
        case "upc":
          return r.upc || r.upcFull || "";
        case "secondUom":
          return r.secondUomDisplay || "-";
        case "thirdUom":
          return r.thirdUomDisplay || "-";
        case "createdBy":
          return (
            r.userName ||
            (r.createdBy && r.createdBy !== "-" && r.createdBy !== "NOT_SET"
              ? r.createdBy
              : "ERPAG Sync")
          );
        case "createdAt":
          return r.createdAt && r.createdAt !== "-"
            ? new Date(r.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-";
        default:
          return "";
      }
    });

    const textToCopy = values.join("\r\n");
    if (!textToCopy.trim()) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedColumnKey(columnKey);
      setTimeout(() => setCopiedColumnKey(null), 2000);
    } catch (err) {
      console.error("Failed to copy column", err);
    }
  };

  const getGeneratedFormatColumnValues = (columnKey) => {
    const formatRows = Array.isArray(viewingRecord?.formatRows)
      ? viewingRecord.formatRows
      : [];

    return formatRows.map((row) => {
      switch (columnKey) {
        case "format":
          return String(row?.indicator ?? "");
        case "unit":
          return String(row?.unit || "");
        case "scc14":
          return String(row?.scc14 || "");
        case "gs1128":
          return String(row?.gs1128 || "");
        default:
          return "";
      }
    });
  };

  const handleCopyGeneratedFormatColumn = async (columnKey) => {
    const values = getGeneratedFormatColumnValues(columnKey);
    const dataToCopy = values.join("\r\n");
    if (!dataToCopy.trim()) return;

    try {
      await navigator.clipboard.writeText(dataToCopy);
      setCopiedFormatColumn(columnKey);
    } catch (error) {
      // no-op: keep UI silent if clipboard access is blocked
    }
  };

  useEffect(() => {
    if (!copiedFormatColumn) return undefined;
    const timer = setTimeout(() => {
      setCopiedFormatColumn(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, [copiedFormatColumn]);

  useEffect(() => {
    if (!viewingRecord) {
      setCopiedFormatColumn(null);
    }
  }, [viewingRecord]);

  const openEditSkuModal = (record) => {
    setEditingRecord(record);
    setEditingSku(String(record?.sku || "").trim());
    setSkuEditError("");
  };

  const closeEditSkuModal = () => {
    if (savingSku) return;
    setEditingRecord(null);
    setEditingSku("");
    setSkuEditError("");
  };

  const handleSaveSkuEdit = async () => {
    const nextSku = String(editingSku || "").trim();
    if (!/^\d{10}$/.test(nextSku)) {
      setSkuEditError("SKU must be numeric and exactly 10 digits.");
      return;
    }
    if (!editingRecord) return;

    const fallbackScc14 =
      String(editingRecord?.scc14 || "").trim() ||
      String(
        Array.isArray(editingRecord?.formatRows)
          ? editingRecord.formatRows.find((row) => row?.scc14)?.scc14 || ""
          : "",
      ).trim();

    setSavingSku(true);
    setSkuEditError("");
    try {
      const response = await updateBarcodeRegistrySku({
        sku: nextSku,
        upcFull: String(editingRecord?.upcFull || editingRecord?.upc || "").trim(),
        scc14: fallbackScc14,
      });

      if (!response?.success) {
        setSkuEditError(response?.message || "Failed to update SKU.");
        return;
      }

      closeEditSkuModal();
      onRefresh();
    } catch (error) {
      setSkuEditError(error?.message || "Failed to update SKU.");
    } finally {
      setSavingSku(false);
    }
  };


  const selectedCount = selectedIds.size;
  const selectedRecords = useMemo(
    () =>
      [...selectedIds]
        .sort((a, b) => a - b)
        .map((index) => currentItemsWithUomType[index])
        .filter(Boolean),
    [currentItemsWithUomType, selectedIds],
  );
  const pageSelectedCount = currentItems.filter((_, idx) =>
    selectedIds.has(idx),
  ).length;
  const allPageSelected =
    pageSelectedCount === currentItems.length && currentItems.length > 0;

  const handleDeleteRow = async () => {
    if (!deletingRecord || !onDeleteRow) return;
    setDeletingRow(true);
    setDeleteError("");
    try {
      await onDeleteRow(deletingRecord);
      setDeletingRecord(null);
      onRefresh();
    } catch (err) {
      setDeleteError(err?.message || "Delete failed");
    } finally {
      setDeletingRow(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRecords.length || !onDeleteRow) return;
    setDeletingSelected(true);
    setDeleteError("");
    try {
      for (const record of selectedRecords) {
        await onDeleteRow(record);
      }
      setShowBulkDeleteConfirm(false);
      setSelectedIds(new Set());
      onRefresh();
    } catch (err) {
      setDeleteError(err?.message || "Bulk delete failed");
    } finally {
      setDeletingSelected(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-2xl font-bold text-gray-800">Barcode Registry</h2>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                setSelectedIds(new Set());
                onRefresh();
              }}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition disabled:opacity-50"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            {safeUpcs.length > 0 && (
              <button
                onClick={handleCopySelected}
                disabled={selectedCount === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-medium shadow-sm text-sm text-white ${
                  selectedCount === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-black hover:bg-gray-700"
                }`}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied
                  ? "Copied!"
                  : `Copy ${selectedCount > 0 ? selectedCount : ""} Selected`}
              </button>
            )}

            {safeUpcs.length > 0 && (
              <button
                onClick={handleExportMissingToExcel}
                disabled={exportingMissing}
                className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg transition font-medium shadow-sm text-sm"
                title="Bulk export all records across database that are Not in ERPAG"
              >
                {exportingMissing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Exporting All...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export Missing to ERPAG
                  </>
                )}
              </button>
            )}

            {safeUpcs.length > 0 && (
              <button
                onClick={handleExportFilteredToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg transition font-medium shadow-sm text-sm"
                title="Export all currently filtered rows to Excel"
              >
                <Download size={16} />
                Export Excel
              </button>
            )}

            {safeUpcs.length > 0 && isAdmin && (
              <button
                onClick={() => {
                  setDeleteError("");
                  setShowBulkDeleteConfirm(true);
                }}
                disabled={selectedCount === 0 || deletingSelected}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-medium shadow-sm text-sm text-white ${
                  selectedCount === 0 || deletingSelected
                    ? "bg-red-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                <Trash2 size={16} />
                Delete {selectedCount > 0 ? selectedCount : ""} Selected
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleFind();
              }
            }}
            placeholder="Search by SKU, UPC, Name EN or Product Category"
            className="w-full sm:max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <div ref={categoryRef} className="relative w-full sm:w-72">
  <button
    type="button"
    onClick={() => setIsCategoryOpen((prev) => !prev)}
    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white text-left flex justify-between"
  >
    {selectedCategory === "all" ? "All Categories" : selectedCategory}
      <ChevronDown
    size={20}
    
    className={`transition-transform text-black  font-bold ${
      isCategoryOpen ? "rotate-180" : ""
    }` }
  />
  </button>

  {isCategoryOpen && (
    <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-300 bg-white shadow-lg max-h-40 overflow-y-auto no-scrollbar">
      {categoryOptions.map((category) => (
        <button
          key={category}
          type="button"
          onClick={() => {
            setSelectedCategory(category);
            setIsCategoryOpen(false);
            if (searchDebounceTimerRef.current) {
              clearTimeout(searchDebounceTimerRef.current);
            }
            onQueryChange({
              search: searchInput,
              category,
              systemFilter: selectedSystemFilter,
              erpagFilter: selectedErpagFilter,
            });
            setCurrentPage(1);
          }}
          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
        >
          {category === "all" ? "All Categories" : category}
        </button>
      ))}
    </div>
  )}
</div>
          <select
            value={selectedSystemFilter}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedSystemFilter(val);
              if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current);
              }
              onQueryChange({
                search: searchInput,
                category: selectedCategory,
                systemFilter: val,
                erpagFilter: selectedErpagFilter,
              });
              setCurrentPage(1);
            }}
            className="w-full sm:w-48 rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Barcodes</option>
            <option value="system">System Generated</option>
            <option value="bom">UPC DB (Non-System)</option>
          </select>
          <select
            value={selectedErpagFilter}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedErpagFilter(val);
              if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current);
              }
              onQueryChange({
                search: searchInput,
                category: selectedCategory,
                systemFilter: selectedSystemFilter,
                erpagFilter: val,
              });
              setCurrentPage(1);
            }}
            className="w-full sm:w-48 rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="all">All ERPAG Status</option>
            <option value="exists">Exists in ERPAG</option>
            <option value="not_exists">Not in ERPAG (Flagged)</option>
          </select>
          <select
            value={selectedUomBarcodeFilter}
            onChange={(e) => {
              setSelectedUomBarcodeFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full sm:w-56 rounded-lg border border-indigo-300 font-medium text-indigo-900 bg-indigo-50/50 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400"
            title="Filter by barcode existence per UOM"
          >
            <option value="all">UOM Barcodes: All</option>
            <option value="missing_any">⚠️ Needs Creation (Any UOM)</option>
            <option value="missing_base">Needs Base UPC (un./kg)</option>
            <option value="missing_second">Needs Second UOM (10 / CS)</option>
            <option value="missing_third">Needs Third UOM (20 / Master)</option>
            <option value="missing_other">Needs Other UOMs</option>
          </select>
          <button
            onClick={handleFind}
            className="w-full sm:w-auto px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
          >
            Find
          </button>
          <button
            onClick={handleClearFilters}
            className="w-full sm:w-auto px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            Clear
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-5 rounded-lg">
          {error}
        </div>
      )}

      {!loading && !error && Number(totalRecords) === 0 && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl border border-dashed">
          No matching UPC records found
        </div>
      )}

      {!loading && Number(totalRecords) > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectPage}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSort("sku")}
                        className="flex items-center gap-1 hover:text-black transition"
                        title="Sort by SKU"
                      >
                        <span>SKU</span>
                        {sortField === "sku" ? (
                          sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-black" /> : <ArrowDown className="w-3.5 h-3.5 text-black" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("sku")}
                        className="text-gray-400 hover:text-black transition ml-1"
                        title="Copy SKU column"
                      >
                        {copiedColumnKey === "sku" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap min-w-[200px] max-w-[280px]">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSort("name")}
                        className="flex items-center gap-1 hover:text-black transition"
                        title="Sort by Name"
                      >
                        <span>NAME EN</span>
                        {sortField === "name" ? (
                          sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-black" /> : <ArrowDown className="w-3.5 h-3.5 text-black" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("name")}
                        className="text-gray-400 hover:text-black transition ml-1"
                        title="Copy Name column"
                      >
                        {copiedColumnKey === "name" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSort("erpagStatus")}
                        className="flex items-center gap-1 hover:text-black transition"
                        title="Sort by ERPAG Status"
                      >
                        <span>ERPAG STATUS</span>
                        {sortField === "erpagStatus" ? (
                          sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-black" /> : <ArrowDown className="w-3.5 h-3.5 text-black" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("erpagStatus")}
                        className="text-gray-400 hover:text-black transition ml-1"
                        title="Copy ERPAG Status column"
                      >
                        {copiedColumnKey === "erpagStatus" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSort("category")}
                        className="flex items-center gap-1 hover:text-black transition"
                        title="Sort by Product Category"
                      >
                        <span>PRODUCT CATEGORY</span>
                        {sortField === "category" ? (
                          sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-black" /> : <ArrowDown className="w-3.5 h-3.5 text-black" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("category")}
                        className="text-gray-400 hover:text-black transition ml-1"
                        title="Copy Category column"
                      >
                        {copiedColumnKey === "category" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSort("uomType")}
                        className="flex items-center gap-1 hover:text-black transition"
                        title="Sort by UOM Type"
                      >
                        <span>UOM TYPE</span>
                        {sortField === "uomType" ? (
                          sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-black" /> : <ArrowDown className="w-3.5 h-3.5 text-black" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("uomType")}
                        className="text-gray-400 hover:text-black transition ml-1"
                        title="Copy UOM Type column"
                      >
                        {copiedColumnKey === "uomType" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSort("upc")}
                        className="flex items-center gap-1 hover:text-black transition"
                        title="Sort by Base UPC"
                      >
                        <span>BASE UPC</span>
                        {sortField === "upc" ? (
                          sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-black" /> : <ArrowDown className="w-3.5 h-3.5 text-black" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("upc")}
                        className="text-gray-400 hover:text-black transition ml-1"
                        title="Copy Base UPC column"
                      >
                        {copiedColumnKey === "upc" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span>SECOND UOM (10)</span>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("secondUom")}
                        className="text-gray-400 hover:text-black transition"
                        title="Copy Second UOM column"
                      >
                        {copiedColumnKey === "secondUom" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span>THIRD UOM (20)</span>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("thirdUom")}
                        className="text-gray-400 hover:text-black transition"
                        title="Copy Third UOM column"
                      >
                        {copiedColumnKey === "thirdUom" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    OTHER UOMs
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSort("createdBy")}
                        className="flex items-center gap-1 hover:text-black transition"
                        title="Sort by Created By"
                      >
                        <span>CREATED BY</span>
                        {sortField === "createdBy" ? (
                          sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-black" /> : <ArrowDown className="w-3.5 h-3.5 text-black" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("createdBy")}
                        className="text-gray-400 hover:text-black transition ml-1"
                        title="Copy Created By column"
                      >
                        {copiedColumnKey === "createdBy" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSort("createdAt")}
                        className="flex items-center gap-1 hover:text-black transition"
                        title="Sort by Created At"
                      >
                        <span>CREATED AT</span>
                        {sortField === "createdAt" ? (
                          sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-black" /> : <ArrowDown className="w-3.5 h-3.5 text-black" />
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyColumn("createdAt")}
                        className="text-gray-400 hover:text-black transition ml-1"
                        title="Copy Created At column"
                      >
                        {copiedColumnKey === "createdAt" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentItemsWithUomType.map((record, rowIndex) => (
                  <tr
                    key={`${record?.upcFull || record?.upc || "row"}-${rowIndex}`}
                    className={`group hover:bg-gray-50/70 transition-colors ${rowIndex % 2 ? "bg-white" : "bg-gray-50/30"}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(rowIndex)}
                        onChange={() => toggleSelect(rowIndex)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-5 py-3 font-mono text-gray-700 whitespace-nowrap">{record.sku}</td>
                    <td 
                      className="px-5 py-3 text-gray-700 min-w-[200px] max-w-[320px]" 
                      title={record.nameEn || record.description}
                    >
                      <div className="truncate group-hover:whitespace-normal group-hover:overflow-visible font-medium transition-all duration-150 cursor-default">
                        {record.nameEn || record.description}
                      </div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {record.rowErpagStatus === "fully_exists" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <Check size={12} className="text-emerald-600" />
                          Exists in ERPAG
                        </span>
                      )}
                      {record.rowErpagStatus === "pending_barcodes" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200" title="SKU exists in ERPAG, but some barcodes need to be added/created">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          Pending Barcodes
                        </span>
                      )}
                      {record.rowErpagStatus === "not_in_erpag" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                          <X size={12} className="text-rose-600" />
                          Not in ERPAG
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{record.productCategory || record.category}</td>
                    <td className="px-5 py-3 text-gray-700 font-medium whitespace-nowrap">
                      {record.uomTypeDisplay}
                    </td>
                    <td className="px-5 py-3 font-mono font-semibold text-indigo-700 whitespace-nowrap">
                      <div>
                        <span>{record.upc || record.upcFull}</span>
                        {(record.upc || record.upcFull) && (
                          <span
                            className={`select-none text-[10px] font-semibold block mt-0.5 ${
                              record.baseBarcodeExists ? "text-emerald-600" : "text-rose-600"
                            }`}
                          >
                            ● {record.baseBarcodeExists ? "Exists" : "Create"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                      {record.secondUomEntry ? (
                        <div>
                          <div className="flex items-center gap-1">
                            <span className="font-semibold text-gray-900">
                              {record.secondUomEntry.scc14 || "-"}
                            </span>
                            <span className="select-none px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              {record.secondUomEntry.unit || "CS"}
                            </span>
                          </div>
                          {record.secondUomEntry.scc14 && (
                            <span
                              className={`select-none text-[10px] font-semibold block mt-0.5 ${
                                record.secondBarcodeExists ? "text-emerald-600" : "text-rose-600"
                              }`}
                            >
                              ● {record.secondBarcodeExists ? "Exists" : "Create"}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                      {record.thirdUomEntry ? (
                        <div>
                          <div className="flex items-center gap-1">
                            <span className="font-semibold text-gray-900">
                              {record.thirdUomEntry.scc14 || "-"}
                            </span>
                            <span className="select-none px-1.5 py-0.5 rounded text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                              {record.thirdUomEntry.unit || "Master"}
                            </span>
                          </div>
                          {record.thirdUomEntry.scc14 && (
                            <span
                              className={`select-none text-[10px] font-semibold block mt-0.5 ${
                                record.thirdBarcodeExists ? "text-emerald-600" : "text-rose-600"
                              }`}
                            >
                              ● {record.thirdBarcodeExists ? "Exists" : "Create"}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {record.otherUoms && record.otherUoms.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-1.5">
                            <div className="flex items-center gap-1 font-mono text-xs text-gray-700">
                              <span className="font-semibold text-gray-900">
                                {record.otherUoms[0].scc14 || "-"}
                              </span>
                              <span className="select-none px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                {record.otherUoms[0].unit || `UOM (${record.otherUoms[0].indicator})`}
                              </span>
                            </div>
                            {record.otherUoms.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setViewingRecord(record)}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 transition"
                                title="Click to view all UOMs"
                              >
                                +{record.otherUoms.length - 1} more
                              </button>
                            )}
                          </div>
                          {record.otherUoms[0].scc14 && (
                            <span
                              className={`select-none text-[10px] font-semibold block mt-0.5 ${
                                record.otherUoms[0].existsInErpag ? "text-emerald-600" : "text-rose-600"
                              }`}
                            >
                              ● {record.otherUoms[0].existsInErpag ? "Exists" : "Create"}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-700 font-medium whitespace-nowrap">
                      {record.userName || (record.createdBy && record.createdBy !== "-" && record.createdBy !== "NOT_SET" ? record.createdBy : "ERPAG Sync")}
                    </td>
                    <td className="px-5 py-3 text-gray-600 font-normal whitespace-nowrap">
                      {record.createdAt && record.createdAt !== "-" ? (
                        new Date(record.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-6 py-3">
                        <div className="flex items-center gap-1">
                          {record.hasFormatCodes ? (
                            <button
                              type="button"
                              title="View Barcodes"
                              onClick={() => setViewingRecord(record)}
                              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition"
                            >
                              <Eye size={16} />
                            </button>
                          ) : (
                            <span className="w-7" />
                          )}
                          <button
                            type="button"
                            title={canEditSku ? "Edit SKU" : "No permission to edit SKU"}
                            onClick={() => openEditSkuModal(record)}
                            disabled={!canEditSku}
                            className={`p-1.5 rounded-lg transition ${
                              canEditSku
                                ? "text-amber-600 hover:bg-amber-50"
                                : "text-gray-300 cursor-not-allowed"
                            }`}
                          >
                            <Pencil size={16} />
                          </button>
                          {isAdmin && (
                            <button
                              type="button"
                              title="Delete Record"
                              onClick={() => {
                                setDeleteError("");
                                setDeletingRecord(record);
                              }}
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 transition"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* <div className="px-6 py-4 bg-gray-50 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">{selectedCount} selected</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>

              <span className="text-sm font-medium px-3">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div> */}
          <div className="px-6 py-4 bg-gray-50 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
  {/* Left Side */}
  <div className="flex items-center gap-4">
    <div className="text-sm text-gray-500">
      {selectedCount} selected
    </div>

    {/* Page Size Dropdown */}
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500">
        Show:
      </span>

      <select
        value={itemsPerPage}
        onChange={(e) => {
          const newSize = Number(e.target.value);
          setItemsPerPage(newSize);
          setCurrentPage(1);
          if (onPageSizeChange) {
            onPageSizeChange(newSize);
          }
        }}
        className="h-9 px-3 pr-8 border border-gray-300 rounded-xl
          bg-white text-sm text-gray-700
          focus:outline-none focus:ring-2 focus:ring-gray-200
          cursor-pointer"
      >
        <option value={50}>50</option>
        <option value={100}>100</option>
        <option value={150}>150</option>
        <option value={200}>200</option>
      </select>

      <span className="text-sm text-gray-500">
        per page
      </span>
    </div>
  </div>

  {/* Pagination */}
  <div className="flex items-center gap-2 flex-wrap">

    {/* First */}
    <button
      type="button"
      onClick={() => setCurrentPage(1)}
      disabled={currentPage === 1}
      className="px-3 py-2 border border-gray-300 rounded-xl text-sm
        text-gray-700 bg-white
        disabled:opacity-40 disabled:cursor-not-allowed
        hover:bg-gray-50 transition"
    >
      First
    </button>

    {/* Previous */}
    <button
      type="button"
      onClick={() =>
        setCurrentPage((p) => Math.max(1, p - 1))
      }
      disabled={currentPage === 1}
      className="px-3 py-2 border border-gray-300 rounded-xl text-sm
        text-gray-700 bg-white
        disabled:opacity-40 disabled:cursor-not-allowed
        hover:bg-gray-50
        flex items-center gap-1 transition"
    >
      <ChevronLeft size={14} />
      Prev
    </button>

    {/* Page Info */}
    <span className="text-sm font-medium text-gray-700 px-3 whitespace-nowrap">
      Page {currentPage} of {totalPages}
    </span>

    {/* Next */}
    <button
      type="button"
      onClick={() =>
        setCurrentPage((p) => Math.min(totalPages, p + 1))
      }
      disabled={currentPage === totalPages}
      className="px-3 py-2 border border-gray-300 rounded-xl text-sm
        text-gray-700 bg-white
        disabled:opacity-40 disabled:cursor-not-allowed
        hover:bg-gray-50
        flex items-center gap-1 transition"
    >
      Next
      <ChevronRight size={14} />
    </button>

    {/* Last */}
    <button
      type="button"
      onClick={() => setCurrentPage(totalPages)}
      disabled={currentPage === totalPages}
      className="px-3 py-2 border border-gray-300 rounded-xl text-sm
        text-gray-700 bg-white
        disabled:opacity-40 disabled:cursor-not-allowed
        hover:bg-gray-50 transition"
    >
      Last
    </button>
  </div>
</div>
        </div>
      )}

      {viewingRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Generated Formats
                </h3>
                <p className="text-sm text-gray-600">
                  SKU {viewingRecord?.sku || "-"} - {viewingRecord?.nameEn || viewingRecord?.description || "Item"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewingRecord(null)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              {Array.isArray(viewingRecord.formatRows) &&
              viewingRecord.formatRows.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <span>Format</span>
                            <button
                              type="button"
                              onClick={() => handleCopyGeneratedFormatColumn("format")}
                              className="text-gray-500 hover:text-black"
                              aria-label="Copy Format column"
                              title="Copy Format column"
                            >
                              {copiedFormatColumn === "format" ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <span>Unit</span>
                            <button
                              type="button"
                              onClick={() => handleCopyGeneratedFormatColumn("unit")}
                              className="text-gray-500 hover:text-black"
                              aria-label="Copy Unit column"
                              title="Copy Unit column"
                            >
                              {copiedFormatColumn === "unit" ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <span>SCC-14</span>
                            <button
                              type="button"
                              onClick={() => handleCopyGeneratedFormatColumn("scc14")}
                              className="text-gray-500 hover:text-black"
                              aria-label="Copy SCC-14 column"
                              title="Copy SCC-14 column"
                            >
                              {copiedFormatColumn === "scc14" ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <span>GS1-128</span>
                            <button
                              type="button"
                              onClick={() => handleCopyGeneratedFormatColumn("gs1128")}
                              className="text-gray-500 hover:text-black"
                              aria-label="Copy GS1-128 column"
                              title="Copy GS1-128 column"
                            >
                              {copiedFormatColumn === "gs1128" ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {viewingRecord.formatRows.map((row) => (
                        <tr key={`fmt-${viewingRecord?.sku || "sku"}-${row.indicator}`}>
                          <td className="px-4 py-3 font-medium text-gray-800">{row.indicator}</td>
                          <td className="px-4 py-3 text-gray-700">{row.unit || "-"}</td>
                          <td className="px-4 py-3 font-mono text-gray-800">
                            <div>
                              <span>{row.scc14 || "-"}</span>
                              {row.scc14 && (
                                <span
                                  className={`text-[10px] font-semibold block mt-0.5 ${
                                    row.existsInErpag ? "text-emerald-600" : "text-rose-600"
                                  }`}
                                >
                                  ● {row.existsInErpag ? "Exists" : "Create"}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-800">
                            <div>
                              <span>{row.gs1128 || "-"}</span>
                              {row.gs1128 && (
                                <span
                                  className={`text-[10px] font-semibold block mt-0.5 ${
                                    row.existsInErpag ? "text-emerald-600" : "text-rose-600"
                                  }`}
                                >
                                  ● {row.existsInErpag ? "Exists" : "Create"}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  No format barcode details available for this row.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {editingRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit SKU</h3>
                <p className="text-sm text-gray-600">
                  Update barcode association for this generated record.
                </p>
              </div>
              <button
                type="button"
                onClick={closeEditSkuModal}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="space-y-1">
                <p className="text-xs text-gray-500">Current SKU</p>
                <p className="font-mono text-sm text-gray-800">
                  {String(editingRecord?.sku || "").trim() || "-"}
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  New SKU
                </label>
                <input
                  value={editingSku}
                  onChange={(e) => setEditingSku(e.target.value)}
                  placeholder="5014150001"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm"
                />
                <p className="text-xs text-gray-500">
                  SKU must be exactly 10 numeric digits.
                </p>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Ensure the SKU is also updated in ERPAG if applicable. To avoid duplicates
                </div>
              </div>
              {skuEditError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {skuEditError}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeEditSkuModal}
                  disabled={savingSku}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveSkuEdit}
                  disabled={savingSku}
                  className="px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-900 disabled:opacity-50"
                >
                  {savingSku ? "Saving..." : "Save SKU"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deletingRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-red-700">Delete Record</h3>
                <p className="text-sm text-gray-600 mt-1">
                  This will permanently delete the barcode entry from the registry.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setDeletingRecord(null); setDeleteError(""); }}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-1 text-sm">
                <div><span className="font-medium text-gray-600">SKU:</span> <span className="font-mono">{deletingRecord?.sku || "-"}</span></div>
                <div><span className="font-medium text-gray-600">UPC:</span> <span className="font-mono text-indigo-700">{deletingRecord?.upc || deletingRecord?.upcFull || "-"}</span></div>
                <div><span className="font-medium text-gray-600">Name:</span> {deletingRecord?.nameEn || deletingRecord?.description || "-"}</div>
              </div>
              {deleteError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{deleteError}</div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setDeletingRecord(null); setDeleteError(""); }}
                  disabled={deletingRow}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteRow}
                  disabled={deletingRow}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deletingRow ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-red-700">
                  Delete Selected Records
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  This will permanently delete {selectedRecords.length} selected barcode
                  {selectedRecords.length === 1 ? " entry" : " entries"} from the registry.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setShowBulkDeleteConfirm(false); setDeleteError(""); }}
                disabled={deletingSelected}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition disabled:opacity-50"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
                <p className="font-medium text-gray-700">
                  Selected records: {selectedRecords.length}
                </p>
                <p className="text-gray-500 mt-1">
                  Only records selected on the current page will be deleted.
                </p>
              </div>
              {deleteError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{deleteError}</div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowBulkDeleteConfirm(false); setDeleteError(""); }}
                  disabled={deletingSelected}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={deletingSelected || selectedRecords.length === 0}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deletingSelected ? "Deleting..." : "Yes, Delete Selected"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
